import React, { useRef, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'

import { Hospital } from 'domain/entities/hospital-model'
import { DoctorCouncilRegister } from 'domain/entities/council-register-model'
import { AddDoctor } from 'domain/usecases/doctor/add-doctor'
import * as yup from 'yup'
import 'main/config/yup'
import * as S from './styles'
import { cpfMask, phoneMask } from 'presentation/utils/masks'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'

import { ReactComponent as TrashIcon } from 'presentation/assets/icons/trashIcon.svg'
import ActualPage from 'presentation/shared/components/ActualPage'
import Heading from 'presentation/shared/components/Heading'
import SupportText from 'presentation/shared/components/SupportText'
import Modal from 'presentation/shared/components/Modal'
import MultipleSelect, {
  MultipleSelectRefProps
} from 'presentation/shared/components/MultipleSelect'
import Table from 'presentation/shared/components/Table'
import { brasilStates } from 'presentation/utils/brazil-states'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'

type AddDoctorFormProps = {
  addDoctor?: AddDoctor
  initialValues?: AddDoctorFormFields
  hospitals?: Hospital[]
} & WithLoadingProps

const AddDoctorForm = WithLoading(
  ({
    addDoctor,
    initialValues = {} as AddDoctorFormFields,
    hospitals,
    setIsLoading
  }: AddDoctorFormProps) => {
    const history = useHistory()
    const [handleSuccessModal, setHandleSuccessModal] = useState(false)
    const multipleSelectRef = useRef<MultipleSelectRefProps>(null)
    const [crmList, setCrmList] = useState<DoctorCouncilRegister[]>([])

    const handleOnAddCRM = () => {
      formik.setFieldTouched('addCrm', true)
      formik.setFieldTouched('addCrmUf', true)
      if (!formik.errors.addCrm && !formik.errors.addCrmUf) {
        setCrmList((prevState) => [
          ...prevState,
          {
            crm: formik.values.addCrm.trim(),
            crmUf: formik.values.addCrmUf,
            isMain: prevState.length ? false : true
          }
        ])
        formik.setFieldValue('addCrm', '')
        formik.setFieldValue('addCrmUf', '')
      }
    }

    const handleOnRemoveCrm = (item: DoctorCouncilRegister) => {
      const updatedList = crmList.filter((i) => i.crm !== item.crm)
      if (updatedList.length) updatedList[0].isMain = true
      setCrmList(updatedList)
    }

    useEffect(() => {
      if (crmList.length) {
        formik.setFieldValue('crm', crmList[0].crm)
        formik.setFieldValue('crmUf', crmList[0].crmUf)
        formik.setFieldValue('councilRegister', crmList)
      } else {
        formik.setFieldValue('crm', '')
        formik.setFieldValue('crmUf', '')
        formik.setFieldValue('councilRegister', '')
      }
      setTimeout(() => {
        formik.setFieldTouched('crm', true)
        formik.setFieldTouched('crmUf', true)
        formik.setFieldTouched('councilRegister', true)
      }, 100)
    }, [crmList])

    const formik = useFormik({
      initialValues: initialValues,
      validateOnMount: true,
      validationSchema: AddDoctorValidation,
      onSubmit: async (values) => {
        const requestObject: AddDoctor.Params = {
          crm: values.crm,
          crmUf: values.crmUf,
          councilRegister: values.councilRegister,
          email: values.email,
          phone: values.phone.replace(/\D+/g, ''),
          name: values.name.trim() + ' ' + values.lastName.trim(),
          cpf: values.cpf.replace(/\D+/g, ''),
          hospital_id: values.hospital_id.map((val) => Number(val))
        }

        try {
          setIsLoading(true)
          await addDoctor?.add(requestObject)
          setHandleSuccessModal(true)
          formik.resetForm()
          multipleSelectRef?.current?.resetMultipleSelect?.()
          setCrmList([])
        } catch (error: any) {
          toast.error(error.message)
        } finally {
          setIsLoading(false)
        }
      }
    })

    return (
      <S.Wrapper role="form" onSubmit={formik.handleSubmit}>
        <ActualPage text="Voltar" onClick={history.goBack} />
        <Heading color="primary" size="xlarge" style={{ marginTop: '10px' }}>
          Novo Médico
        </Heading>
        <SupportText style={{ marginTop: '10px' }} color="gray">
          Envio de convite para novos médicos
        </SupportText>
        <S.Content>
          <TextFieldNew
            label="Nome"
            name="name"
            value={formik.values.name || ''}
            onInputChange={formik.handleChange('name')}
            onBlur={formik.handleBlur('name')}
            error={formik.touched.name && formik.errors.name}
          />
          <TextFieldNew
            label="Sobrenome"
            name="lastName"
            value={formik.values.lastName || ''}
            onInputChange={formik.handleChange('lastName')}
            onBlur={formik.handleBlur('lastName')}
            error={formik.touched.lastName && formik.errors.lastName}
          />
          <TextFieldNew
            label="Celular"
            name="phone"
            value={formik.values.phone || ''}
            onInputChange={formik.handleChange('phone')}
            mask={phoneMask}
            onBlur={formik.handleBlur('phone')}
            error={formik.touched.phone && formik.errors.phone}
          />
          <TextFieldNew
            label="CPF"
            name="cpf"
            value={formik.values.cpf || ''}
            onInputChange={formik.handleChange('cpf')}
            mask={cpfMask}
            onBlur={formik.handleBlur('cpf')}
            error={formik.touched.cpf && formik.errors.cpf}
          />
          <TextFieldNew
            label="E-mail"
            name="email"
            value={formik.values.email || ''}
            onInputChange={formik.handleChange('email')}
            onBlur={formik.handleBlur('email')}
            error={formik.touched.email && formik.errors.email}
          />
          <MultipleSelect
            label="Unidades"
            name="hospital_id"
            error={formik.touched.hospital_id && formik.errors.hospital_id}
            items={hospitals?.map(({ name, friendly_name, hospital_id }) => ({
              label: friendly_name! || name!,
              value: hospital_id!
            }))}
            onBlur={formik.handleBlur('hospital_id')}
            onCheck={formik.handleChange('hospital_id')}
            ref={multipleSelectRef}
          />
        </S.Content>
        <S.CrmContainer>
          <S.AddCrm>
            <SelectFieldNew
              label="UF"
              name="addCrmUf"
              value={formik.values.addCrmUf || ''}
              initialValue={''}
              onInputChange={formik.handleChange('addCrmUf')}
              onBlur={formik.handleBlur('addCrmUf')}
              error={formik.touched.addCrmUf && formik.errors.addCrmUf}
              items={brasilStates}
              required
            />
            <TextFieldNew
              label="CRM"
              name="addCrm"
              value={formik.values.addCrm || ''}
              onInputChange={formik.handleChange('addCrm')}
              onBlur={formik.handleBlur('addCrm')}
              error={formik.touched.addCrm && formik.errors.addCrm}
              required
            />
            <ButtonNew
              disabled={!formik.values.addCrm || !formik.values.addCrmUf}
              onClick={handleOnAddCRM}
              style={{ alignSelf: 'flex-end' }}
              type="button"
              size="large"
            >
              Adicionar
            </ButtonNew>
          </S.AddCrm>
          <S.CrmList>
            <Table
              columns={columns}
              data={crmList?.map((item) => ({
                crmUf: item.crmUf,
                crm: item.crm,
                delete: (
                  <S.DeleteButton
                    onClick={() => handleOnRemoveCrm(item)}
                    type="button"
                  >
                    <TrashIcon />
                  </S.DeleteButton>
                )
              }))}
              pagination={false}
            />
          </S.CrmList>
        </S.CrmContainer>
        <ButtonNew
          disabled={!formik.isValid || formik.isSubmitting}
          style={{
            marginTop: '32px',
            alignSelf: 'flex-end',
            justifySelf: 'flex-end',
            width: '100%',
            maxWidth: '320px'
          }}
          type="submit"
          size="large"
        >
          Enviar Convite
        </ButtonNew>
        <Modal
          title="Médico cadastrado com sucesso!"
          show={handleSuccessModal}
          close={() => setHandleSuccessModal(false)}
        />
      </S.Wrapper>
    )
  }
)

export default AddDoctorForm

const columns = [
  {
    name: 'crmUf',
    label: 'Estado',
    options: {
      sort: false
    }
  },
  {
    name: 'crm',
    label: 'CRM',
    options: {
      sort: false
    }
  },
  {
    name: 'delete',
    label: ' ',
    options: {
      sort: false
    }
  }
]

const AddDoctorValidation = yup.object().shape({
  name: yup.string().required(),
  lastName: yup.string().required(),
  phone: yup
    .string()
    .transform((value) => value.replace(/\D/g, '')) // remove máscara
    .matches(/^\d{4}9\d{8}$/, 'Telefone inválido')
    .required(),
  email: yup.string().email().required(),
  cpf: yup.string().cpf().required(),
  addCrm: yup.string().nullable(),
  addCrmUf: yup.string().nullable(),
  crm: yup.string().required(),
  crmUf: yup.string().required(),
  councilRegister: yup.array().min(1, 'Adicione CRM do médico').required(),
  hospital_id: yup.array().min(1, 'Campo obrigatório').required()
})

export type AddDoctorFormFields = {
  name: string
  lastName: string
  phone: string
  email: string
  cpf: string
  addCrm: string
  addCrmUf: string
  crm: string
  crmUf: string
  councilRegister: DoctorCouncilRegister[]
  hospital_id: string[]
}
